import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import { twMerge } from 'tailwind-merge'
import { useDebouncedAutosave } from '@renderer/hooks/use-debounced-autosave'
import { useReminderActions, type ReminderItem } from '../../../hooks/use-reminders'
import { isCaretOnFirstLine } from '../utils/caret-line'

interface ReminderNotesProps {
  item: ReminderItem
  /** After a change the host wants to flash as saved. */
  onSaving?: () => void
  fieldRef?: RefObject<HTMLTextAreaElement>
  /** Arrow Up on the notes' first line, where the host has a field above them. */
  onArrowUpOut?: () => void
  className?: string
}

/**
 * The user's own notes on a reminder, saved once typing pauses and again as the
 * host unmounts. Shared by the row's popover and the Reminder View, so both
 * edit the same field through the same autosave.
 *
 * Plain text, not markdown: notes are a scribble beside a one-line to-do, and
 * a rich editor only raised questions it couldn't answer — a pasted URL became
 * a link with no way to open it, and round-tripping the document rewrote what
 * the user typed.
 */
export function ReminderNotes({ item, onSaving, fieldRef, onArrowUpOut, className }: ReminderNotesProps) {
  const { setUserDescription } = useReminderActions()
  const stored = item.userDescription ?? ''
  const [draft, setDraft] = useState(stored)
  const ownRef = useRef<HTMLTextAreaElement>(null)
  const field = fieldRef ?? ownRef

  // The actions change identity with every mutation, and the autosave flushes whenever its
  // save function does, so it gets one stable function that calls the latest actions.
  const latest = useRef({ setUserDescription, onSaving })
  latest.current = { setUserDescription, onSaving }
  const persist = useCallback(
    (text: string) => {
      latest.current.onSaving?.()
      return latest.current.setUserDescription(item.id, text)
    },
    [item.id],
  )
  const { schedule, markSaved } = useDebouncedAutosave(persist)

  // What this field last sent, as the API will hand it back: `setUserDescription`
  // trims, so a draft ending in a space comes home shorter than it left.
  const sent = useRef(stored)

  useEffect(() => {
    // What is already on the reminder is already saved, so a draft that still
    // matches it never triggers a write.
    markSaved(stored)
    // This field's own write echoing back. Adopting it would delete the space
    // the user is still typing after, or undo the keystrokes since.
    if (stored === sent.current) return
    // Someone else's edit, but the caret is in here: their text wins until
    // they leave the field, including a failed save rolling the cache back.
    if (field.current && document.activeElement === field.current) return
    sent.current = stored
    setDraft(stored)
  }, [stored, markSaved, field])

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (onArrowUpOut && event.key === 'ArrowUp' && !event.shiftKey && isCaretOnFirstLine(event.currentTarget)) {
      event.preventDefault()
      onArrowUpOut()
    }
  }

  return (
    // Fills the space down to the footer, and a press anywhere in it writes at the end of the notes.
    <div
      className={className ?? 'flex shrink-0 grow cursor-text flex-col'}
      onMouseDown={(event) => {
        const input = field.current
        if (!input || event.target === input) return
        event.preventDefault()
        input.focus()
        input.setSelectionRange(input.value.length, input.value.length)
      }}
    >
      <textarea
        ref={field}
        aria-label="Notes"
        placeholder="Add notes…"
        value={draft}
        rows={1}
        onChange={(event) => {
          setDraft(event.target.value)
          sent.current = event.target.value.trim()
          schedule(event.target.value)
        }}
        onKeyDown={handleKeyDown}
        // Grows with the text and never scrolls itself: the host's body is the scroller.
        className={twMerge(
          'text-foreground type-prose-sm placeholder:text-foreground-placeholder w-full flex-1 resize-none break-words bg-transparent p-0 [field-sizing:content] focus-visible:outline-none',
        )}
      />
    </div>
  )
}
